/**
 * Reviews showcase – counters on scroll + deferred/staggered review image loading
 */
(function () {
  const SECTION_SELECTOR = '.reviews-showcase[data-section-id]';
  const STAGGER_MS = 60;
  const LOAD_ROOT_MARGIN = '400px 0px';

  const compactNF =
    typeof Intl !== 'undefined' && Intl.NumberFormat
      ? new Intl.NumberFormat(undefined, {
          notation: 'compact',
          maximumFractionDigits: 1
        })
      : null;

  function formatValue(el, value) {
    const n = Math.max(0, Math.round(value));

    if (el.classList.contains('reviews-showcase__counter--compact')) {
      return (compactNF ? compactNF.format(n) : String(n)) + '+';
    }

    if (el.classList.contains('reviews-showcase__counter--plus')) {
      return n + '+';
    }

    if (el.classList.contains('reviews-showcase__counter--stars')) {
      return n + (n >= 1 ? ' Star' : '');
    }

    return String(n);
  }

  function animateCounter(el, end, durationSec) {
    const start = 0;
    const t0 = performance.now();
    const durationMs = durationSec * 1000;

    function step(t) {
      const p = Math.min(1, (t - t0) / durationMs);
      const current = start + (end - start) * p;
      el.textContent = formatValue(el, current);
      if (p < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  function isInView(el) {
    const rect = el.getBoundingClientRect();
    return rect.top <= window.innerHeight && rect.bottom >= 0;
  }

  function markImageLoaded(img) {
    const onReady = () => img.classList.add('is-loaded');

    if (img.complete && img.naturalWidth > 0) {
      onReady();
    } else {
      img.addEventListener('load', onReady, { once: true });
      img.addEventListener('error', onReady, { once: true });
    }
  }

  function activateImage(img) {
    if (img.dataset.reviewsActivated === 'true') return;
    img.dataset.reviewsActivated = 'true';

    const deferredSrc = img.getAttribute('data-src');
    if (deferredSrc) {
      img.src = deferredSrc;
      img.removeAttribute('data-src');
    }

    markImageLoaded(img);
  }

  function duplicateTrackItems(track) {
    if (track.dataset.duplicated === 'true') return;

    const items = track.querySelectorAll('.reviews-showcase__item:not(.reviews-showcase__item--clone)');
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('reviews-showcase__item--clone');
      clone.removeAttribute('id');

      const cloneImg = clone.querySelector('img');
      const sourceImg = item.querySelector('img');
      if (cloneImg && sourceImg) {
        cloneImg.removeAttribute('data-src');
        if (sourceImg.src && !sourceImg.getAttribute('data-src')) {
          cloneImg.src = sourceImg.src;
        }
        if (sourceImg.classList.contains('is-loaded')) {
          cloneImg.classList.add('is-loaded');
        }
      }

      track.appendChild(clone);
    });

    track.dataset.duplicated = 'true';
  }

  function loadReviewImages(columns) {
    if (columns.dataset.reviewsImagesReady === 'true') return;
    columns.dataset.reviewsImagesReady = 'true';
    columns.classList.remove('reviews-showcase__columns--pending');

    const imgs = Array.from(
      columns.querySelectorAll('.reviews-showcase__item:not(.reviews-showcase__item--clone) .reviews-showcase__img')
    );
    const tracks = columns.querySelectorAll('.reviews-showcase__track');

    imgs.forEach((img, index) => {
      if (img.getAttribute('data-src')) {
        setTimeout(() => activateImage(img), index * STAGGER_MS);
      } else {
        markImageLoaded(img);
      }
    });

    const cloneDelay = imgs.length * STAGGER_MS + 100;
    setTimeout(() => {
      tracks.forEach(duplicateTrackItems);
    }, cloneDelay);
  }

  function initCounters(section) {
    if (section.dataset.reviewsCountersReady === 'true') return;

    const trigger = section.querySelector('.reviews-showcase__trigger');
    const counters = Array.from(section.querySelectorAll('.reviews-showcase__counter'));
    if (!trigger || !counters.length) return;

    let started = false;

    function runCounters() {
      if (started) return;
      if (!isInView(trigger)) return;

      started = true;
      counters.forEach((el) => {
        const end = parseInt(el.getAttribute('data-target') || '0', 10);
        animateCounter(el, end, 3);
      });
    }

    const onScroll = () => runCounters();
    window.addEventListener('scroll', onScroll, { passive: true });
    runCounters();

    section.dataset.reviewsCountersReady = 'true';
  }

  function initImageLoading(section) {
    const columns = section.querySelector('[data-reviews-columns]');
    if (!columns || columns.dataset.reviewsObserverReady === 'true') return;

    columns.classList.add('reviews-showcase__columns--pending');

    if (!('IntersectionObserver' in window)) {
      loadReviewImages(columns);
      columns.dataset.reviewsObserverReady = 'true';
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          loadReviewImages(columns);
          observer.disconnect();
        });
      },
      { rootMargin: LOAD_ROOT_MARGIN, threshold: 0 }
    );

    observer.observe(columns);
    columns.dataset.reviewsObserverReady = 'true';
  }

  function initSection(section) {
    initCounters(section);
    initImageLoading(section);
  }

  function initAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section =
      event.target.querySelector(SECTION_SELECTOR) ||
      event.target.closest(SECTION_SELECTOR);
    if (section) {
      delete section.dataset.reviewsCountersReady;
      const columns = section.querySelector('[data-reviews-columns]');
      if (columns) {
        delete columns.dataset.reviewsImagesReady;
        delete columns.dataset.reviewsObserverReady;
        columns.querySelectorAll('.reviews-showcase__track').forEach((track) => {
          delete track.dataset.duplicated;
          track.querySelectorAll('.reviews-showcase__item--clone').forEach((el) => el.remove());
        });
      }
      initSection(section);
    }
  });
})();
